"use client";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Paper,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Link,
  useMediaQuery,
  AppBar,
  Toolbar,
  IconButton,
  Drawer,
  Menu,
  MenuItem,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import {
  CalendarToday,
  Bookmark,
  CalendarMonth,
  LocationOn,
  Flight,
  Terrain,
  Group,
  Info,
  EmojiEvents,
  CardGiftcard,
  WorkspacePremium,
  Star,
  Route,
  Email,
} from "@mui/icons-material";
import FAQSummitPage from "./faq";
import bgSummit from "../../assets/bgSummit.jpeg";
import React, { useState } from "react";
import { useNavigate } from "react-router";
import { useUser } from "@/contexts/UserContext";
import { useSiteAlert } from "@/contexts/SiteAlertContext";
import { LogOutIcon, MenuIcon } from "lucide-react";
import MainHeader from "@/components/MainHeader";

// Custom styled components
const HeroSection = styled(Box)(({ theme }) => ({
  position: "relative",
  padding: theme.spacing(10, 2, 8),
  textAlign: "center",
  minHeight: "50vh",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  backgroundColor: "#e0e9f5",
  overflow: "hidden",

  "&::before": {
    content: "''",
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: `url(${bgSummit})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    opacity: 0.5, // this controls the transparency
    zIndex: 0,
  },

  "& > *": {
    position: "relative",
    zIndex: 1,
  },
}));

const ContentWrapper = styled(Paper)(({ theme }) => ({
  width: "90%",
  maxWidth: "1000px",
  margin: "-50px auto 50px",
  padding: theme.spacing(4, 4),
  borderRadius: "16px",
  boxShadow: "0 8px 20px rgba(0, 0, 0, 0.1)",
  position: "relative",
  zIndex: 2,
  [theme.breakpoints.down("md")]: {
    width: "95%",
    marginTop: "-40px",
    padding: theme.spacing(3, 2),
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: "2em",
  fontWeight: 700,
  color: "#0056b3",
  marginBottom: theme.spacing(4),
  textAlign: "center",
  position: "relative",
  paddingBottom: "12px",
  //   "&::after": {
  //     content: '""',
  //     display: "block",
  //     width: "70px",
  //     height: "3px",
  //     background: "linear-gradient(90deg, #fd7e14, #007bff)",
  //     margin: "8px auto 0",
  //     borderRadius: "2px",
  //   },
  [theme.breakpoints.down("md")]: {
    fontSize: "1.6em",
  },
}));

const EventGlanceTitle = styled(SectionTitle)(({ theme }) => ({
  textAlign: "left",
  display: "flex",
  alignItems: "center",
  //   "&::after": {
  //     margin: "8px 0 0 0",
  //     width: "100px",
  //     background: "#0056b3",
  //   },
  //   "&::before": {
  //     content: '""',
  //     position: "absolute",
  //     left: "30px",
  //     bottom: "12px",
  //     width: "40px",
  //     height: "3px",
  //     background: "#fd7e14",
  //     zIndex: 1,
  //     borderRadius: "2px",
  //   },
  //   [theme.breakpoints.down("md")]: {
  //     textAlign: "center",
  //     justifyContent: "center",
  //     "&::after": {
  //       margin: "8px auto 0",
  //     },
  //     "&::before": {
  //       left: "50%",
  //       transform: "translateX(-50%)",
  //       marginLeft: "-20px",
  //     },
  //   },
}));

const DetailCard = styled(Card)(({ theme }) => ({
  background: "#F9FAFC",
  padding: theme.spacing(2.5),
  borderRadius: "10px",
  border: "1px solid #dee2e6",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
  transition:
    "transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease",
  textAlign: "center",
  height: "100%",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 8px 20px rgba(0, 0, 0, 0.1)",
    borderColor: "#007bff",
  },
}));

const AgendaNoteCard = styled(Card)(({ theme }) => ({
  background: "#ffffff",
  padding: theme.spacing(3),
  borderRadius: "10px",
  border: "1px solid #dee2e6",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
  textAlign: "center",
  height: "fit-content",
}));

const AwardCategory = styled(Paper)(({ theme }) => ({
  background: "#ffffff",
  padding: theme.spacing(2.5),
  borderRadius: "10px",
  marginBottom: theme.spacing(2.5),
  border: "1px solid #dee2e6",
  borderLeft: "4px solid #007bff",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
  "&.top-winners": {
    borderLeftColor: "#28a745",
  },
}));

const ExploreSection = styled(Paper)(({ theme }) => ({
  textAlign: "center",
  padding: theme.spacing(2.5),
  background: "#E9F5FF",
  border: "1px solid #BEE0FF",
  borderRadius: "10px",
  marginTop: theme.spacing(4),
}));

const CTAButton = styled(Button)(({ theme }) => ({
  background: "linear-gradient(90deg, #fd7e14, #FF6F00)",
  color: "#fff",
  padding: theme.spacing(1.5, 4),
  borderRadius: "50px",
  fontSize: "1.05em",
  fontWeight: 600,
  textTransform: "none",
  boxShadow: "0 4px 10px rgba(255, 140, 0, 0.3)",
  "&:hover": {
    transform: "translateY(-3px)",
    boxShadow: "0 6px 15px rgba(255, 140, 0, 0.4)",
    background: "linear-gradient(90deg, #fd7e14, #FF6F00)",
  },
}));

const eventDetails = [
  {
    icon: <Bookmark sx={{ fontSize: "1.8em", color: "#007bff", mb: 1.5 }} />,
    title: "Event Name",
    description: "ThoughtCoCreate Summit (TCC Summit)",
  },
  {
    icon: (
      <CalendarMonth sx={{ fontSize: "1.8em", color: "#007bff", mb: 1.5 }} />
    ),
    title: "Summit Date",
    description: "July 5, 2025",
  },
  {
    icon: <LocationOn sx={{ fontSize: "1.8em", color: "#007bff", mb: 1.5 }} />,
    title: "Summit Venue",
    description:
      "Meeting Room, Sapa Highland Resort and Spa (07 Thac Bac St, Sapa, Lao Cai, Vietnam)",
  },
  {
    icon: <Flight sx={{ fontSize: "1.8em", color: "#007bff", mb: 1.5 }} />,
    title: "Overall Trip Dates",
    description: "July 4 - July 7, 2025 (4-day, 3-night)",
  },
  {
    icon: <Terrain sx={{ fontSize: "1.8em", color: "#007bff", mb: 1.5 }} />,
    title: "Locations",
    description: "Sapa & Hanoi, Vietnam",
  },
  {
    icon: <Group sx={{ fontSize: "1.8em", color: "#007bff", mb: 1.5 }} />,
    title: "Experience",
    description: "Insightful sessions & exploration of Northern Vietnam.",
  },
];
const NavLink = styled(Link)(({ theme }) => ({
  color: "#0066FF",
  textDecoration: "none",
  marginLeft: theme.spacing(3),
  "&:hover": {
    textDecoration: "underline",
  },
  fontFamily: "Roboto",
}));

export default function TCCSummitLanding() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const navigate = useNavigate();
  const { user } = useUser();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { message, isActive } = useSiteAlert();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleUserMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    document.body.classList.remove("ro-popover-open");
    setAnchorEl(event.currentTarget);
  };
  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const navItems = [
    { name: "Overview", href: "./#overview" },
    { name: "Educatalyst", href: "./educatalyst" },
    { name: "Summit Finalist", href: "./summit-finalist" },
    { name: "TCC Summit 2025", href: "./tcc-summit-2025" },
    { name: "How It Works", href: "./#how-it-works" },
    { name: "Timeline", href: "./#timeline" },
    { name: "Judges", href: "./#judges" },
    { name: "Rules", href: "./#rules" },
    { name: "FAQ", href: "./#faq" },
  ];

  const mainNavItems = navItems.slice(0, 5);
  const moreNavItems = navItems.slice(5);

  const [moreAnchorEl, setMoreAnchorEl] = useState<null | HTMLElement>(null);
  const handleMoreClick = (event: React.MouseEvent<HTMLElement>) => {
    setMoreAnchorEl(event.currentTarget);
  };
  const handleMoreClose = () => {
    setMoreAnchorEl(null);
  };

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: "center", p: 2 }}>
      <List>
        {navItems.map((item) => (
          <ListItem
            key={item.name}
            component={Link}
            href={item.href}
            sx={{ color: "#000", textDecoration: "none" }}
          >
            <ListItemText primary={item.name} />
          </ListItem>
        ))}
      </List>
    </Box>
  );
  return (
    <Box sx={{ width: "100%", overflow: "hidden" }}>
      {/* <AppBar
        position="static"
        color="default"
        elevation={1}
        sx={{ backgroundColor: "white" }}
      >
        <Container>
          <Toolbar disableGutters>
            <Box sx={{ flexGrow: 1 }}>
              <img
                src="https://pythaverse.space/wp-content/uploads/2023/02/18b214a6c0c81d9644d9.png"
                alt="Pythaverse Logo"
                width="200"
                height="50"
                style={{ objectFit: "contain" }}
              />
            </Box>

            {isMobile ? (
              <>
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  edge="end"
                  onClick={handleDrawerToggle}
                >
                  <MenuIcon />
                </IconButton>
                <Drawer
                  anchor="right"
                  open={mobileOpen}
                  onClose={handleDrawerToggle}
                  ModalProps={{
                    keepMounted: true,
                  }}
                >
                  {drawer}
                </Drawer>
              </>
            ) : (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {mainNavItems.map((item) => (
                  <NavLink key={item.name} href={item.href} sx={{ mx: 1 }}>
                    {item.name}
                  </NavLink>
                ))}
                {moreNavItems.length > 0 && (
                  <>
                    <Button
                      onClick={handleMoreClick}
                      sx={{ textTransform: "none" }}
                    >
                      More
                    </Button>
                    <Menu
                      anchorEl={moreAnchorEl}
                      open={Boolean(moreAnchorEl)}
                      onClose={handleMoreClose}
                    >
                      {moreNavItems.map((item) => (
                        <MenuItem key={item.name} onClick={handleMoreClose}>
                          <NavLink href={item.href}>{item.name}</NavLink>
                        </MenuItem>
                      ))}
                    </Menu>
                  </>
                )}
                {user?.user_login ? (
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleUserMenuClick}
                    color="primary"
                    title={user?.display_name}
                    sx={{
                      textTransform: "none",
                      borderRadius: "30px",
                      "& .MuiTypography-root": {
                        color: "white",
                      },
                      fontFamily: "Roboto",
                      fontStyle: "normal",
                      fontWeight: 700,
                      fontSize: "16px",
                      lineHeight: "19px",
                      textDecoration: "none",
                      color: "#F5F5F5",
                      marginLeft: "50px",
                    }}
                  >
                    <Typography variant="h6" noWrap component="div" ml={1}>
                      {user?.display_name}
                    </Typography>
                  </Button>
                ) : (
                  <NavLink key="btnLogin" href={user?.login_url}>
                    Login
                  </NavLink>
                )}
              </Box>
            )}

            <Menu
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
              open={Boolean(anchorEl)}
              onClose={handleUserMenuClose}
            >
              <MenuItem
                onClick={() => (window.location.href = user?.logout_url || "/")}
              >
                <LogOutIcon size={20} />
                <Typography variant="inherit" noWrap component="span" ml={1}>
                  Logout
                </Typography>
              </MenuItem>
            </Menu>
          </Toolbar>
        </Container>
      </AppBar> */}
      <MainHeader />
      {/* Hero Section */}
      <HeroSection>
        <Box sx={{ position: "relative", zIndex: 1, maxWidth: "850px" }}>
          <Typography
            variant="h2"
            component="h1"
            sx={{
              fontSize: { xs: "1.9em", md: "2.6em" },
              fontWeight: 800,
              mb: 2,
              color: "#0056b3",
              lineHeight: 1.25,
            }}
          >
            Educator ThoughtCoCreate Summit 2025: <br />
            Sapa & Hanoi Experience
          </Typography>
          <Typography
            variant="h5"
            sx={{
              fontSize: { xs: "1.1em", md: "1.4em" },
              fontWeight: 600,
              mb: 2.5,
              color: "#fd7e14",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}
          >
            Discover. Innovate. Connect.
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: "0.95em", md: "1.05em" },
              fontWeight: 400,
              color: "#fff",
              maxWidth: "700px",
              mx: "auto",
              lineHeight: 1.7,
            }}
          >
            Pythaverse is thrilled to invite our esteemed finalists to the
            ThoughtCoCreate (TCC) Summit 2025. Join us for an inspiring journey
            of collaboration and innovation, set against the breathtaking
            backdrop of Sapa's mountains and the vibrant culture of Hanoi.
          </Typography>
        </Box>
      </HeroSection>

      {/* Content Wrapper */}
      <Container maxWidth={false} sx={{ px: 0 }}>
        <ContentWrapper>
          {/* Event at a Glance */}
          <Box sx={{ py: 4, borderBottom: "1px solid #dee2e6" }}>
            <EventGlanceTitle variant="h3">
              <CalendarToday
                sx={{ mr: 1.5, color: "#fd7e14", fontSize: "1.1em" }}
              />
              Event at a Glance
            </EventGlanceTitle>

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 4,
                alignItems: "flex-start",
                flexDirection: { xs: "column", lg: "row" },
              }}
            >
              {/* Event Details Column */}
              <Box sx={{ flex: 2, minWidth: "300px" }}>
                <Grid container spacing={2.5}>
                  {eventDetails.map((detail, index) => (
                    <Grid item xs={12} sm={6} key={index} sx={{ mb: 6 }}>
                      <DetailCard>
                        <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
                          {detail.icon}
                          <Typography
                            variant="h6"
                            component="h4"
                            sx={{
                              fontSize: "1.05em",
                              fontWeight: 600,
                              color: "#0056b3",
                              mb: 1,
                            }}
                          >
                            {detail.title}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "0.9em",
                              color: "#495057",
                              lineHeight: 1.6,
                            }}
                          >
                            {detail.description}
                          </Typography>
                        </CardContent>
                      </DetailCard>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Box>
            {/* Agenda Note Column */}
            <Box sx={{ flex: 1, minWidth: "280px", mt: { xs: 2.5, lg: 0 } }}>
                <AgendaNoteCard>
                  <CardContent sx={{ p: 0, "&:last-child": { pb: 0 }}}>
                    <Box sx={{textAlign: "center"}}>
                      <Info
                        sx={{
                          fontSize: "2.2em",
                          color: "#007bff",
                          display: "flex",
                          justifyContent: "center",
                          margin: "0 auto",
                          mb: 2,
                        }}
                      />
                    </Box>
                    <Typography
                      variant="h6"
                      component="h4"
                      sx={{
                        fontSize: "1.1em",
                        fontWeight: 600,
                        color: "#0056b3",
                        mb: 1.5,
                      }}
                    >
                      Important Agenda Note
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "0.95em",
                        color: "#495057",
                        lineHeight: 1.6,
                      }}
                    >
                      Detailed Summit agenda, including session topics and
                      speaker information, will be released closer to the event
                      date.
                    </Typography>
                  </CardContent>
                </AgendaNoteCard>
              </Box>
          </Box>

          {/* Awards & Recognition */}
          <Box sx={{ py: 4, borderBottom: "1px solid #dee2e6" }}>
            <SectionTitle variant="h3">
              <EmojiEvents sx={{ mr: 1.5, color: "#fd7e14" }} />
              Awards & Recognition
            </SectionTitle>

            <Typography
              variant="body1"
              sx={{
                textAlign: "center",
                fontSize: "1em",
                color: "#495057",
                mb: 3,
              }}
            >
              Pythaverse is proud to celebrate the innovative spirit and
              contributions of all our participants.
            </Typography>

            <AwardCategory>
              <Typography
                variant="h6"
                component="h4"
                sx={{
                  fontSize: "1.2em",
                  fontWeight: 700,
                  color: "#0056b3",
                  mb: 1.5,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <CardGiftcard
                  sx={{ fontSize: "1.1em", mr: 1.5, color: "#fd7e14" }}
                />
                All Invited Summit Attendees
              </Typography>
              <List dense sx={{ pl: 0 }}>
                <ListItem sx={{ pl: 3, alignItems: "flex-start" }}>
                  <ListItemIcon sx={{ minWidth: "25px", mt: 0.5 }}>
                    <Star sx={{ fontSize: "0.8em", color: "#fd7e14" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Will receive a special welcoming gift from Pythaverse upon arrival as a token of our appreciation for your participation."
                    primaryTypographyProps={{
                      fontSize: "0.95em",
                      color: "#495057",
                      lineHeight: 1.6,
                    }}
                  />
                </ListItem>
              </List>
            </AwardCategory>

            <AwardCategory>
              <Typography
                variant="h6"
                component="h4"
                sx={{
                  fontSize: "1.2em",
                  fontWeight: 700,
                  color: "#0056b3",
                  mb: 1.5,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <WorkspacePremium
                  sx={{ fontSize: "1.1em", mr: 1.5, color: "#fd7e14" }}
                />
                All Finalists
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: "0.85em",
                  fontStyle: "italic",
                  color: "#6c757d",
                  mb: 1,
                }}
              >
                In recognition of your excellent work, each finalist will be
                honored with:
              </Typography>
              <List dense sx={{ pl: 0 }}>
                {[
                  "A TCC Summit Award.",
                  "A Certificate of Participation.",
                  "A Personalized Whitepaper Set, developed based on your proposal (compiled and sent post-summit).",
                ].map((item, index) => (
                  <ListItem
                    key={index}
                    sx={{ pl: 3, alignItems: "flex-start" }}
                  >
                    <ListItemIcon sx={{ minWidth: "25px", mt: 0.5 }}>
                      <Star sx={{ fontSize: "0.8em", color: "#fd7e14" }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={item}
                      primaryTypographyProps={{
                        fontSize: "0.95em",
                        color: "#495057",
                        lineHeight: 1.6,
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </AwardCategory>

            <AwardCategory className="top-winners">
              <Typography
                variant="h6"
                component="h4"
                sx={{
                  fontSize: "1.2em",
                  fontWeight: 700,
                  color: "#0056b3",
                  mb: 1.5,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Star sx={{ fontSize: "1.1em", mr: 1.5, color: "#FFD700" }} />
                Top 3 Winners
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: "0.85em",
                  fontStyle: "italic",
                  color: "#6c757d",
                  mb: 1,
                }}
              >
                A highlight of the TCC Summit will be the announcement of our
                Top 3 Winners. These outstanding individuals/teams will be
                presented with:
              </Typography>
              <List dense sx={{ pl: 0 }}>
                <ListItem sx={{ pl: 3, alignItems: "flex-start" }}>
                  <ListItemIcon sx={{ minWidth: "25px", mt: 0.5 }}>
                    <Star sx={{ fontSize: "0.8em", color: "#fd7e14" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Exclusive TCC Summit Trophies during a dedicated awards segment, celebrating their exceptional achievements."
                    primaryTypographyProps={{
                      fontSize: "0.95em",
                      color: "#495057",
                      lineHeight: 1.6,
                    }}
                  />
                </ListItem>
              </List>
            </AwardCategory>
          </Box>

          {/* Explore Vietnam */}
          <Box sx={{ py: 4 }}>
            <ExploreSection>
              <Route
                sx={{
                  color: "#007bff",
                  fontSize: "1.4em",
                  display: "block",
                  mb: 1,
                }}
              />
              <Typography
                variant="h5"
                component="h3"
                sx={{
                  fontWeight: 600,
                  color: "#0056b3",
                  mb: 2,
                }}
              >
                Explore Northern Vietnam
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: "1.05em",
                  color: "#212529",
                  lineHeight: 1.7,
                }}
              >
                This exclusive 4-day, 3-night experience combines insightful
                summit sessions with the opportunity to explore the unique
                beauty of Sapa's mountains and the vibrant culture of Hanoi.
              </Typography>
            </ExploreSection>
          </Box>

          <Box sx={{ py: 4 }}>
            <FAQSummitPage />
          </Box>
        </ContentWrapper>
      </Container>
      {/* Footer CTA */}
      {/* <Box
        sx={{
          textAlign: "center",
          py: 4,
          px: 2.5,
          backgroundColor: "#f4f7fc",
          mt: 4,
        }}
      >
        <Typography
          variant="body1"
          sx={{
            fontSize: "1.1em",
            color: "#495057",
            mb: 2.5,
            lineHeight: 1.6,
          }}
        >
          We are incredibly excited to host you for this memorable event. <br />
          Prepare for an unforgettable journey of learning, networking, and discovery!
        </Typography>
        <CTAButton startIcon={<Email />}>Stay Tuned for More Updates</CTAButton>
      </Box> */}
      {/* Footer */}
      <Box sx={{ py: 3, bgcolor: "#f8f9fa", textAlign: "center", borderTop: "1px solid #ddd" }}>
        <Container>
          <Typography variant="body2" color="text.secondary">
            © 2025 Pythaverse. All Rights Reserved.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
}
